import { useState, useEffect } from "react"

import {
  Box,
  Button,
  Center,
  Divider,
  Grid,
  GridItem,
  SimpleGrid,
  Spinner,
  Text,
  TextProps,
  useToast,
} from "@chakra-ui/react"
import { captureException } from "@sentry/react"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import { EmailCommunicationType } from "api/emailCommunication/getEmailCommunicationDetailApi"
import BackArrowIcon from "components/Icons/BookingIcons/BackArrowIcon"
import ForwardArrowIcon from "components/Icons/ForwardArrowIcon"
import CustomToast from "components/Toast/CustomToast"
import useModulePermission from "hooks/useModulePermission"
// import { generateDatabaseDateTime } from "utils/misc"
import { systemEmailLogClient } from "services/clients/systemEmailLogClient"

import { Endpoints } from "../../types"

const HeadingText = ({
  fontSize,
  widthText,
  text,
  ...rest
}: { fontSize?: string; text: string; widthText?: string } & TextProps) => {
  return (
    <Text
      fontSize={{ base: "16px", md: fontSize || "18px" }}
      marginRight={2}
      fontWeight={600}
      lineHeight={1.3}
      {...rest}
    >
      {text}
    </Text>
  )
}

const DetailText = ({
  text,
  ...rest
}: {
  text: string | number | null | undefined
}) => {
  return (
    <Text
      wordBreak={"break-word"}
      fontSize={{ base: "16px", md: "18px" }}
      fontWeight={400}
      lineHeight={1.3}
      {...rest}
    >
      {text}
    </Text>
  )
}

const statusText: Record<string, string> = {
  SEND: "Send",
  REJECT: "Reject",
  BOUNCE: "Bounce",
  COMPLAINT: "Complaint",
  DELIVERY: "Delivery",
  OPEN: "Open",
  CLICK: "Click",
  // RENDERING_FAILURE: "Rendering failure",
  // DELIVERY_DELAY: "Delivery delay",
  SUBSCRIPTION: "Subscription",
}

const SystemEmailLogDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const hasAccessToModule = useModulePermission("EmailCommunication")
  const [loading, setLoading] = useState<boolean>(true)
  const [emailCommunicationData, setEmailCommunicationData] =
    useState<EmailCommunicationType>()
  const [finalHtml, setFinalHtml] = useState("")

  // This function will take you back to the previous page in the browser's history
  const goBack = () => {
    window.history.back()
  }

  // get email communication data
  const getEmailCommunicationData = async (emailCommunicationId: string) => {
    await systemEmailLogClient
      .getEmailLogDetail({
        emailCommunicationId,
      })
      .then((response) => {
        setEmailCommunicationData(response.data.data)
        if (response.data.apiStatus === "ERROR") {
          toast({
            render: () => (
              <CustomToast message={"Failed to get email details!"} />
            ),
          })
        }
        setLoading(false)
        return true
      })
      .catch((err) => {
        console.error(err)
        setLoading(false)
        captureException(
          new Error(
            `Error emailCommunicationClient.getEmailCommunicationDetail ${JSON.stringify(
              err,
            )}`,
          ),
        )
      })
  }

  // AWS URL
  const AWS_URL =
    "https://whimstay-production-assets.s3.us-east-1.amazonaws.com/"

  useEffect(() => {
    if (id) {
      void getEmailCommunicationData(id)
    } else {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (!emailCommunicationData?.text) return

    let html = emailCommunicationData.text

    // Match all cid:... entries
    const cidMatches = html.match(/cid:[\w-]+/g) // e.g., cid:90d13b6a-ed69-47eb-8381-1ce46d527d70

    if (cidMatches?.length && emailCommunicationData.attachments?.length) {
      cidMatches.forEach((fullCidRef) => {
        const cid = fullCidRef.replace("cid:", "")

        // Find the first attachment that includes this cid in the path
        const matchedAttachment = emailCommunicationData.attachments.find(
          (path) => path.includes(cid),
        )

        if (matchedAttachment) {
          const imageUrl = `${AWS_URL}${matchedAttachment}`
          const regex = new RegExp(`cid:${cid}`, "g")
          html = html.replace(regex, imageUrl)
        } else {
          console.warn(`No attachment matched for cid: ${cid}`)
        }
      })
    }

    setFinalHtml(html)
  }, [emailCommunicationData])

  if (
    !hasAccessToModule.permitedActions.includes(
      "viewEmailCommunication" as never,
    )
  ) {
    return <Navigate to={Endpoints.propertyDashboard} replace />
  }
  return (
    <Box
      background='rgb(255, 255, 255)'
      borderRadius='12px'
      height='fit-content'
      overflowY={"scroll"}
      w='100%'
      padding={{ base: "8px", lg: "8px" }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        marginBottom={"10px"}
      >
        <Box cursor={"pointer"} onClick={goBack}>
          <BackArrowIcon />
        </Box>
      </Box>

      {loading && (
        <Center p={8}>
          <Spinner size={"xl"} />
        </Center>
      )}

      {!loading && emailCommunicationData && (
        <Grid templateColumns='repeat(5, 1fr)' gap={4}>
          <GridItem colSpan={{ base: 12 }} marginTop={"18px"}>
            <SimpleGrid columns={{ base: 1 }}>
              {/*<!------ Email Information section ----!>*/}
              <Box>
                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='Booking ID:' />
                  <DetailText
                    text={emailCommunicationData?.bookingNumber || "-"}
                  />
                </Box>

                {/* <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='Sent:' />
                  <DetailText
                    text={
                      emailCommunicationData.createdDt
                        ? generateDatabaseDateTime(
                            emailCommunicationData.createdDt,
                            true,
                            true,
                          )
                        : "-"
                    }
                  />
                </Box>

                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='Recieved:' />
                  <DetailText
                    text={
                      emailCommunicationData.createdDt
                        ? generateDatabaseDateTime(
                            emailCommunicationData.createdDt,
                            true,
                            true,
                          )
                        : "-"
                    }
                  />
                </Box> */}

                {/* {emailCommunicationData?.status && (
                  <Box
                    display={"flex"}
                    alignItems={"end"}
                    marginBottom={"11px"}
                  >
                    <HeadingText text='Status:' />
                    <Tag
                      bg={`tag.${emailCommunicationData?.status?.toUpperCase()}`}
                      color='#fff'
                    >
                      {
                        statusText[
                          emailCommunicationData?.status?.toUpperCase()
                        ]
                      }
                    </Tag>
                  </Box>
                )} */}

                <Divider my='4' />

                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='From:' />
                  <DetailText
                    text={emailCommunicationData?.sender}
                  />
                </Box>

                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='To:' />
                  <DetailText
                    text={emailCommunicationData?.recipient}
                  />
                </Box>

                {emailCommunicationData?.cc &&
                  emailCommunicationData?.cc?.length > 0 && (
                    <Box display={"flex"} marginBottom={"11px"}>
                      <HeadingText text='Cc:' />
                      <DetailText text={emailCommunicationData?.cc} />
                    </Box>
                  )}

                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='Subject:' />
                  <DetailText text={emailCommunicationData?.subject || "-"} />
                </Box>

                <Box display={"flex"} marginBottom={"11px"}>
                  <HeadingText text='Error Message:' />
                  <DetailText
                    text={emailCommunicationData?.errorMessage || "NA"}
                  />
                </Box>
              </Box>
              <Box
                mt={6}
                width='100%'
                dangerouslySetInnerHTML={{
                  __html: finalHtml || "",
                }}
              />
            </SimpleGrid>
          </GridItem>
        </Grid>
      )}

      {!loading && !emailCommunicationData && (
        <Box display='flex' justifyContent='center' alignItems='center' p={20}>
          <Text
            fontSize='16px'
            color={"textColor"}
            fontWeight={500}
            whiteSpace='nowrap'
          >
            No Data Found
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default SystemEmailLogDetails
